/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
  ParameterBase,
  MultiplicityType,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType, DataType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { SLIDE_NUMBER } from "./constants";
import { clone, uniq } from "../../../../shared";
import { DictionariesService } from "@sa/rep-mdm";
import { getDefaultMark, Parameters } from './utils/getDefaultMark';

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  protected _exportParameters = new Map<string, ParameterBase>();

  protected cube = new BehaviorSubject<CubeDefinition | null>(null);
  public olapData = new BehaviorSubject<OLAPResult | null>(null);

  public orderedIds$ = new BehaviorSubject<number[]>([]);
  public comments$ = new BehaviorSubject<string[]>([]);
  public sources$ = new BehaviorSubject<string | null>(null);
  public loading$ = new BehaviorSubject<boolean>(true);

  protected _parameters = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters() {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    const exportParameters = [
      "@e_calendar", "@e_countries", "@e_commodities"
    ];
    for (const eParamName of exportParameters) {
      let eParameter = this.options.dataflowService.getParameter(eParamName);
      if (eParameter === undefined) {
        this.options.dataflowService.parameters.add({
          id: eParamName,
          multiplicityType: MultiplicityType.Multiple,
          type: DataType.Number,
          allowNullable: false,
          value: []
        });
        eParameter = this.options.dataflowService.getParameter(eParamName);
      }
      this._exportParameters.set(eParamName, eParameter!);
    }

    this._parameters.pipe(debounceTime(200)).subscribe(async parameters => {
      if (parameters) {
        this.readyState = ComponentContextReadyState.Loading;
        this.loading$.next(true);
        this.olapData.next(null);

        const { serviceProvider } = this.options;
        const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
        parameters = await getDefaultMark(dictionariesService, parameters);
        this.orderedIds$.next(parameters.orderedCommodities);
        this.comments$.next(parameters.comments);

        const olapDataPromise = this.query(parameters);
        const sourcesPromise = this.fetchSources(parameters);

        const [olapData, sources] = await Promise.all([olapDataPromise, sourcesPromise]);

        this.sources$.next(sources);
        this.olapData.next(olapData);
        this.readyState = ComponentContextReadyState.Loaded;
        this.loading$.next(false);

        const eCountriesParameter = this._exportParameters.get("@e_countries")! as MultiValueParameter;
        eCountriesParameter.value = parameters.countries;
        const eCalendarParameter = this._exportParameters.get("@e_calendar")! as MultiValueParameter;
        eCalendarParameter.value = [(Number(parameters.calendar[0]) - 10000), ...parameters.calendar];
        const eCommoditiesParameter = this._exportParameters.get("@e_commodities")! as MultiValueParameter;
        eCommoditiesParameter.value = parameters.displayCommodities;
      }
    });
  }

  private async _initDefaultMark() {
    const { dataflowService } = this.options;

    const calendarParameter = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const businessParameter = dataflowService.getParameter("@business-units") as SingleValueParameter;
    const commoditiesParam = dataflowService.getParameter("@commodities") as MultiValueParameter;
    const attibuteParam = dataflowService.getParameter("@attributes") as MultiValueParameter;
    const unitParam = dataflowService.getParameter("@units") as MultiValueParameter;
    const countriesParam = dataflowService.getParameter("@countries") as MultiValueParameter;

    let parameters: Partial<Parameters> = {
      businessUnits: businessParameter.value as number,
      countries: countriesParam.value as number[],
      calendar: calendarParameter.value as number[],
      commodities: commoditiesParam.value as number[],
      attributes: attibuteParam.value as number[],
      units: unitParam.value as number[],
    };

    const { serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
    parameters = await getDefaultMark(dictionariesService, parameters as Parameters);
    this._parameters.next(parameters as Parameters);
  }

  async update(command: UpdateCommand): Promise<void> {
    const business = command.values.get("@business-units") as number;

    if (business !== undefined) {
      const parameters = clone(this._parameters.value);
      if (parameters !== undefined) {
        parameters.businessUnits = business;
        this._parameters.next(parameters);
      }
    }
  }

  async fetchSources(parameters: Parameters) {
    const { commodities, businessUnits } = parameters;
    const { dataSourcesService } = this.options;
    const sources = await Promise.all(commodities.map(commodity => dataSourcesService.getDataSources(SLIDE_NUMBER, businessUnits, Number(commodity))));
    return uniq(sources.join(', ').split(', ')).join(', ');
  }

  async query(parameters: Parameters) {
    const { calendar, commodities, countries, attributes, units } = parameters;

    const cubeSlideDef = await dataService.getCubeDefinition("slide_8_9_10_11");
    const cubeParameters = clone(cubeSlideDef.parameters);
    cubeParameters["p_commodities"]!["defaultValue"] = commodities.join(',');
    cubeParameters["p_countries"]!["defaultValue"] = countries.join(',');
    cubeParameters["p_attributes"]!["defaultValue"] = attributes.join(',');
    cubeParameters["p_calendar"]!["defaultValue"] = calendar.join(',');
    const cubeSlide = await dataService.getCube({
      id: "slide_8_9_10_11",
      parameters: cubeParameters,
    });

    const olapData = await cubeSlide.query({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendar,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "units.default", selection: units, selectionMode: SelectionMode.Include },
          { path: "attributes.default", selection: attributes, selectionMode: SelectionMode.Include },
          {
            path: "countries.default",
            selection: countries,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          {
            path: "commodities.default",
            selection: commodities,
            selectionMode: SelectionMode.Include,
          },
          {
            path: "Measures", measures: [
              { id: "change", aggregationType: AggregationType.Max }
            ]
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);
    return olapData;
  }
}
